import { Form, Select, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { useDistrictOptions, useRegionOptions } from './useGeoOptions'

interface RegionDistrictFieldsProps {
  regionRequired?: boolean
}

/**
 * Manzil shaklidagi viloyat/tuman tanlagichlari (GEO §6).
 *
 * ⚠️ QO'SHISH YO'Q — faqat tanlash. Yangi viloyat/tuman `/geo/regions` va
 * `/geo/districts` sahifalarida ochiladi.
 *
 * ⚠️ Qiymat sifatida ID emas, NOM saqlanadi: `/api/sites` kontrakti
 * `region`/`district` ni SATR sifatida qabul qiladi va uni o'zgartirish
 * server shartnomasiga tegish bo'lardi. Nom tenant ichida yagona
 * (`lower(btrim(name))` bo'yicha unikal), ya'ni nom ↔ yozuv bir qiymatli.
 */
export function RegionDistrictFields({ regionRequired = false }: RegionDistrictFieldsProps) {
  const { t } = useTranslation()
  const form = Form.useFormInstance()
  const regionName = Form.useWatch<string | undefined>('region', form)
  const districtName = Form.useWatch<string | undefined>('district', form)

  const regions = useRegionOptions(true)
  const regionId = regions.options.find((r) => r.name === regionName)?.id ?? null
  const districts = useDistrictOptions(regionId, true)

  const regionOptions = withLegacy(
    regions.options.map((r) => r.name),
    regionName,
    t('geo.notInRegistry'),
  )
  const districtOptions = withLegacy(
    districts.options.map((d) => d.name),
    districtName,
    t('geo.notInRegistry'),
  )

  const registryEmpty = !regions.isPending && !regions.isError && regions.options.length === 0

  return (
    <>
      <Form.Item
        name="region"
        label={t('geo.region')}
        extra={
          registryEmpty ? (
            <Typography.Text type="warning">{t('geo.registryEmpty')}</Typography.Text>
          ) : regions.truncated ? (
            <Typography.Text type="warning">{t('geo.optionsTruncated')}</Typography.Text>
          ) : (
            t('geo.selectOnlyHint')
          )
        }
        rules={
          regionRequired ? [{ required: true, message: t('geo.validation.regionRequired') }] : []
        }
      >
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          loading={regions.isPending}
          placeholder={t('geo.regionPlaceholder')}
          options={regionOptions}
          // ⚠️ Viloyat o'zgarsa tuman TOZALANADI. Usiz ekranda mos kelmaydigan
          // juftlik qolib, server 400 berardi va sabab ko'rinmasdi.
          onChange={() => form.setFieldValue('district', undefined)}
        />
      </Form.Item>

      <Form.Item name="district" label={t('geo.district')}>
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          // Viloyat tanlanmaguncha o'chiq: tuman ro'yxati viloyatsiz ma'nosiz
          disabled={regionId === null}
          loading={districts.isPending}
          placeholder={t(regionId === null ? 'geo.districtDisabled' : 'geo.districtPlaceholder')}
          options={districtOptions}
        />
      </Form.Item>
    </>
  )
}

/**
 * Reyestrda yo'q joriy qiymatni ro'yxatga qo'shadi.
 *
 * ⚠️ Reyestrgacha yozilgan manzillarda erkin matn turibdi. Uni tanlovga
 * qo'shmasak, tahrirlash oynasi maydonni BO'SH ko'rsatardi va birinchi
 * saqlashda mavjud qiymat jimgina yo'qolardi.
 */
function withLegacy(names: readonly string[], current: string | undefined, note: string) {
  const options = names.map((name) => ({ value: name, label: name }))
  if (current && current !== '' && !names.includes(current)) {
    options.unshift({ value: current, label: `${current} — ${note}` })
  }
  return options
}
